import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '../design-system';
import { apiService } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { withAlpha } from '../../theme';

type RecordAction = 'created' | 'updated' | 'reactivated' | 'skipped' | 'removed';

interface SyncOutcome {
  synced?: number;
  updated?: number;
  reactivated?: number;
  skipped?: number;
  removed?: number;
  failed?: number;
  fetchedRows?: number;
  failures?: Array<{ row: number; error: string; externalId?: string; label?: string; recordId?: string }>;
  records?: Array<{ action: RecordAction; recordId: string; row: number; externalId?: string; label?: string;
    prices?: { before: { price: number | null; salePrice: number | null } | null;
      mapped: { price?: number | null; salePrice?: number | null };
      saved: { price: number | null; salePrice: number | null };
      final?: { price: number | null; salePrice: number | null }; verified: boolean } }>;
  recordsTruncated?: boolean;
  log?: string[];
}

interface ExecutionLog {
  _id: string;
  executionType: 'scheduled' | 'manual';
  status: 'pending' | 'running' | 'success' | 'failed';
  startedAt: string;
  completedAt?: string;
  duration?: number;
  result?: any;
  error?: string;
  stackTrace?: string;
  retryAttempt?: number;
}

interface Props {
  taskId: string;
  /** False when the list has no schedule — "next run" then reads "manual only". */
  scheduled: boolean;
}

const POLL_MS = 4000;
const RECORD_PAGE = 50;

/** Older runs stored the outcome under result.syncOutcome; newer ones flatten it into result. */
const outcomeOf = (run: ExecutionLog): SyncOutcome | undefined => {
  const r = run.result;
  if (!r || typeof r !== 'object') return undefined;
  if (r.syncOutcome) return r.syncOutcome;
  if (typeof r.synced === 'number' || typeof r.failed === 'number') return r;
  return undefined;
};

const formatDuration = (ms?: number): string => {
  if (ms === undefined || ms === null || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

/**
 * Runs tab of a webhook-fed Data List: the next scheduled run (in the
 * viewer's local time — the server keeps UTC), a background "Sync now", and
 * the execution history with counts, the records each run touched, failures,
 * the run log and, for failed runs, the error + stack trace.
 */
export const DataListSyncRuns: React.FC<Props> = ({ taskId, scheduled }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t, locale } = useLocale();
  const lang = (locale || 'en').split('_')[0];

  const [runs, setRuns] = useState<ExecutionLog[]>([]);
  const [nextRunAt, setNextRunAt] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  // Set right after "Sync now" until the server's running log shows up, so
  // polling starts even before the first refresh sees it.
  const [awaitingStart, setAwaitingStart] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);
  const mounted = useRef(true);

  const formatDate = useCallback(
    (iso?: string) => (iso ? new Date(iso).toLocaleString(lang, { dateStyle: 'medium', timeStyle: 'short' } as any) : '—'),
    [lang]
  );

  const relative = useCallback(
    (iso: string) => {
      const diff = new Date(iso).getTime() - Date.now();
      const mins = Math.round(Math.abs(diff) / 60000);
      const text = mins < 60 ? `${mins}m` : mins < 1440 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
      return diff >= 0 ? t('dataLists.runs.inTime', { time: text }) : t('dataLists.runs.dueNow');
    },
    [t]
  );

  const refresh = useCallback(async () => {
    try {
      const [history, task] = await Promise.all([
        apiService.getTaskExecutionHistory(taskId, 30),
        apiService.getTaskById(taskId),
      ]);
      if (!mounted.current) return;
      if (history.statusCode === 1000 && Array.isArray(history.data)) {
        setRuns(history.data);
        if (history.data.some((r: ExecutionLog) => r.status === 'running')) setAwaitingStart(false);
      }
      if (task.statusCode === 1000 && task.data) setNextRunAt(task.data.nextExecutionAt || undefined);
    } catch (err) {
      console.error('[DataListSyncRuns] refresh failed:', err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  const activeRun = runs.find((r) => r.status === 'running');
  const polling = !!activeRun || awaitingStart;

  useEffect(() => {
    if (!polling) return;
    const timer = setInterval(refresh, POLL_MS);
    // Give up waiting for a run that never registered (e.g. task rejected).
    const giveUp = awaitingStart ? setTimeout(() => setAwaitingStart(false), 30000) : undefined;
    return () => {
      clearInterval(timer);
      if (giveUp) clearTimeout(giveUp);
    };
  }, [polling, awaitingStart, refresh]);

  const handleSyncNow = async () => {
    setMessage(null);
    setStarting(true);
    try {
      const res = await apiService.executeTask(taskId, { background: true });
      if (res.statusCode !== 1000) throw new Error(res.errors?.[0]?.message || t('dataLists.schema.webhook.syncNowFailed'));
      if (res.data?.alreadyRunning) {
        setMessage({ kind: 'info', text: t('dataLists.runs.alreadyRunning') });
      } else {
        setMessage({ kind: 'info', text: t('dataLists.runs.started') });
        setAwaitingStart(true);
      }
      await refresh();
    } catch (err: any) {
      setMessage({ kind: 'error', text: err?.message || t('dataLists.schema.webhook.syncNowFailed') });
    } finally {
      setStarting(false);
    }
  };

  const textStart = { textAlign: rtl.textStart } as const;

  return (
    <View>
      {/* Next run + Sync now */}
      <View style={styles.card}>
        <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="time-outline" size={20} color={colors.primary as string} />
          <View style={styles.flex}>
            <Text style={[styles.cardLabel, textStart]}>{t('dataLists.runs.nextRun')}</Text>
            {scheduled && nextRunAt ? (
              <Text style={[styles.cardValue, textStart]}>
                {formatDate(nextRunAt)} <Text style={styles.muted}>· {relative(nextRunAt)}</Text>
              </Text>
            ) : (
              <Text style={[styles.cardValue, textStart]}>{scheduled ? '—' : t('dataLists.runs.manualOnly')}</Text>
            )}
          </View>
        </View>

        {activeRun ? (
          <View style={[styles.runningBanner, { flexDirection: rtl.rowDirection }]}>
            <ActivityIndicator size="small" color={colors.primary as string} />
            <Text style={[styles.runningText, textStart]}>
              {t('dataLists.runs.runningSince', { time: formatDate(activeRun.startedAt) })}
            </Text>
          </View>
        ) : null}

        <View style={{ marginTop: spacing.sm }}>
          <Button
            title={activeRun || awaitingStart ? t('dataLists.runs.syncRunning') : t('dataLists.schema.webhook.syncNow')}
            onPress={handleSyncNow}
            disabled={starting || !!activeRun || awaitingStart}
            loading={starting}
            variant="outline"
          />
        </View>
        <Text style={[styles.hint, textStart]}>{t('dataLists.runs.syncNowHint')}</Text>
        {message && (
          <Text style={[styles.hint, textStart, message.kind === 'error' && { color: colors.error as string }]}>{message.text}</Text>
        )}
      </View>

      {/* History */}
      <View style={[styles.historyHeader, { flexDirection: rtl.rowDirection }]}>
        <Text style={[styles.sectionTitle, textStart]}>{t('dataLists.runs.history')}</Text>
        <TouchableOpacity onPress={refresh} hitSlop={8} accessibilityLabel={t('dataLists.runs.refresh')}>
          <Ionicons name="refresh" size={18} color={colors.primary as string} />
        </TouchableOpacity>
      </View>

      {loading ? (
        <ActivityIndicator style={{ marginTop: spacing.lg }} color={colors.primary as string} />
      ) : runs.length === 0 ? (
        <Text style={[styles.hint, textStart]}>{t('dataLists.runs.empty')}</Text>
      ) : (
        runs.map((run) => (
          <RunItem
            key={run._id}
            run={run}
            expanded={expandedId === run._id}
            onToggle={() => setExpandedId(expandedId === run._id ? null : run._id)}
            formatDate={formatDate}
          />
        ))
      )}
    </View>
  );
};

const RunItem: React.FC<{
  run: ExecutionLog;
  expanded: boolean;
  onToggle: () => void;
  formatDate: (iso?: string) => string;
}> = ({ run, expanded, onToggle, formatDate }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const [recordFilter, setRecordFilter] = useState<RecordAction | 'all'>('all');
  const [recordLimit, setRecordLimit] = useState(RECORD_PAGE);
  const [showStack, setShowStack] = useState(false);
  const outcome = outcomeOf(run);
  const textStart = { textAlign: rtl.textStart } as const;

  const statusMeta = {
    success: { icon: 'checkmark-circle', color: colors.status.success as string, label: t('dataLists.runs.status.success') },
    failed: { icon: 'close-circle', color: colors.error as string, label: t('dataLists.runs.status.failed') },
    running: { icon: 'sync-circle', color: colors.primary as string, label: t('dataLists.runs.status.running') },
    pending: { icon: 'ellipse-outline', color: colors.text.secondary as string, label: t('dataLists.runs.status.pending') },
  }[run.status] || { icon: 'ellipse-outline', color: colors.text.secondary as string, label: run.status };
  // A run that finished but with per-row failures is "partial", not a clean success.
  const partial = run.status === 'success' && (outcome?.failed ?? 0) > 0;

  const counts: Array<{ key: string; label: string; value?: number; tone?: 'error' }> = outcome
    ? [
        { key: 'fetched', label: t('dataLists.runs.counts.fetched'), value: outcome.fetchedRows ?? run.result?.fetchedRows },
        { key: 'created', label: t('dataLists.runs.counts.created'), value: outcome.synced },
        { key: 'updated', label: t('dataLists.runs.counts.updated'), value: outcome.updated },
        { key: 'reactivated', label: t('dataLists.runs.counts.reactivated'), value: outcome.reactivated },
        { key: 'skipped', label: t('dataLists.runs.counts.skipped'), value: outcome.skipped },
        { key: 'removed', label: t('dataLists.runs.counts.removed'), value: outcome.removed },
        { key: 'failed', label: t('dataLists.runs.counts.failed'), value: outcome.failed, tone: 'error' },
      ].filter((c) => typeof c.value === 'number') as any
    : [];

  const records = outcome?.records || [];
  const filteredRecords = recordFilter === 'all' ? records : records.filter((r) => r.action === recordFilter);
  const actionsPresent = Array.from(new Set(records.map((r) => r.action)));

  const summary = outcome
    ? t('dataLists.runs.summary', {
        created: outcome.synced ?? 0,
        updated: outcome.updated ?? 0,
        removed: outcome.removed ?? 0,
        failed: outcome.failed ?? 0,
      })
    : run.error || '';

  return (
    <View style={styles.runCard}>
      <TouchableOpacity onPress={onToggle} style={[styles.row, { flexDirection: rtl.rowDirection }]} activeOpacity={0.7}>
        <Ionicons name={(partial ? 'alert-circle' : statusMeta.icon) as any} size={22} color={partial ? (colors.warning as string) : statusMeta.color} />
        <View style={styles.flex}>
          <View style={[styles.runTitleRow, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.runTitle, textStart]}>{formatDate(run.startedAt)}</Text>
            <View style={styles.badge}>
              <Text style={styles.badgeText}>
                {run.executionType === 'manual' ? t('dataLists.runs.trigger.manual') : t('dataLists.runs.trigger.scheduled')}
              </Text>
            </View>
          </View>
          <Text style={[styles.runMeta, textStart]} numberOfLines={expanded ? undefined : 1}>
            {partial ? t('dataLists.runs.status.partial') : statusMeta.label}
            {run.status !== 'running' ? ` · ${formatDuration(run.duration ?? (run.completedAt ? new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime() : undefined))}` : ''}
            {(run.retryAttempt ?? 0) > 0 ? ` · ${t('dataLists.runs.attempt', { n: (run.retryAttempt ?? 0) + 1 })}` : ''}
            {summary ? ` · ${summary}` : ''}
          </Text>
        </View>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={colors.text.secondary as string} />
      </TouchableOpacity>

      {expanded && (
        <View style={styles.details}>
          <Text style={[styles.detailLine, textStart]}>
            {t('dataLists.runs.startedAt')}: {formatDate(run.startedAt)}
            {run.completedAt ? `   ${t('dataLists.runs.finishedAt')}: ${formatDate(run.completedAt)}` : ''}
          </Text>

          {counts.length > 0 && (
            <View style={[styles.countsGrid, { flexDirection: rtl.rowDirection }]}>
              {counts.map((c) => (
                <View key={c.key} style={styles.countCell}>
                  <Text style={[styles.countValue, c.tone === 'error' && (c.value ?? 0) > 0 && { color: colors.error as string }]}>{c.value}</Text>
                  <Text style={styles.countLabel}>{c.label}</Text>
                </View>
              ))}
            </View>
          )}

          {run.status === 'failed' && (run.error || run.stackTrace) && (
            <View style={styles.errorBox}>
              <Text style={[styles.subTitle, textStart, { color: colors.error as string }]}>{t('dataLists.runs.error')}</Text>
              {!!run.error && <Text selectable style={[styles.mono, textStart]}>{run.error}</Text>}
              {!!run.stackTrace && (
                <>
                  <TouchableOpacity onPress={() => setShowStack(!showStack)}>
                    <Text style={[styles.link, textStart]}>{showStack ? t('dataLists.runs.hideStack') : t('dataLists.runs.showStack')}</Text>
                  </TouchableOpacity>
                  {showStack && <Text selectable style={[styles.mono, styles.stack]}>{run.stackTrace}</Text>}
                </>
              )}
            </View>
          )}

          {(outcome?.failures?.length ?? 0) > 0 && (
            <View style={styles.block}>
              <Text style={[styles.subTitle, textStart]}>
                {t('dataLists.runs.failures', { count: outcome!.failed ?? outcome!.failures!.length })}
              </Text>
              {outcome!.failures!.map((f, i) => (
                <View key={i} style={styles.listRow}>
                  <Text style={[styles.listPrimary, textStart]}>
                    {f.row >= 0 ? t('dataLists.runs.rowN', { n: f.row + 1 }) : t('dataLists.runs.removal')}
                    {f.label ? ` · ${f.label}` : ''}
                    {f.externalId ? ` · #${f.externalId}` : ''}
                  </Text>
                  <Text selectable style={[styles.listSecondary, textStart, { color: colors.error as string }]}>{f.error}</Text>
                </View>
              ))}
              {(outcome!.failed ?? 0) > outcome!.failures!.length && (
                <Text style={[styles.hint, textStart]}>{t('dataLists.runs.moreNotListed', { count: (outcome!.failed ?? 0) - outcome!.failures!.length })}</Text>
              )}
            </View>
          )}

          {records.length > 0 && (
            <View style={styles.block}>
              <Text style={[styles.subTitle, textStart]}>{t('dataLists.runs.records')}</Text>
              <View style={[styles.chips, { flexDirection: rtl.rowDirection }]}>
                {(['all', ...actionsPresent] as Array<RecordAction | 'all'>).map((a) => (
                  <TouchableOpacity
                    key={a}
                    onPress={() => {
                      setRecordFilter(a);
                      setRecordLimit(RECORD_PAGE);
                    }}
                    style={[styles.chip, recordFilter === a && styles.chipActive]}
                  >
                    <Text style={[styles.chipText, recordFilter === a && styles.chipTextActive]}>
                      {a === 'all' ? t('dataLists.runs.filterAll') : t(`dataLists.runs.counts.${a}`)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              {filteredRecords.slice(0, recordLimit).map((r, i) => (
                <View key={`${r.recordId}-${i}`} style={[styles.recordRow, { flexDirection: rtl.rowDirection }]}>
                  <Text style={[styles.actionTag, actionTone(r.action)]}>{t(`dataLists.runs.counts.${r.action}`)}</Text>
                  <View style={styles.flex}>
                    <Text style={[styles.listPrimary, textStart]} numberOfLines={1}>
                      {r.label || r.externalId || r.recordId}
                    </Text>
                    <Text selectable style={[styles.listSecondary, textStart]} numberOfLines={1}>
                      {r.externalId ? `#${r.externalId} · ` : ''}
                      {r.row >= 0 ? `${t('dataLists.runs.rowN', { n: r.row + 1 })} · ` : ''}
                      {r.recordId}
                    </Text>
                    {r.prices && (
                      <View>
                        <Text selectable style={[styles.listSecondary, textStart]}>
                          {t('dataLists.runs.priceBefore')}: {r.prices.before?.price ?? '—'} / {r.prices.before?.salePrice ?? '—'}
                        </Text>
                        <Text selectable style={[styles.listSecondary, textStart]}>
                          {t('dataLists.runs.priceMapped')}: {r.prices.mapped.price ?? '—'} / {r.prices.mapped.salePrice ?? '—'}
                        </Text>
                        <Text selectable style={[styles.listSecondary, textStart]}>
                          {t('dataLists.runs.priceSaved')}: {r.prices.saved.price ?? '—'} / {r.prices.saved.salePrice ?? '—'}
                        </Text>
                        {r.prices.final && <Text selectable style={[styles.listSecondary, textStart]}>
                          {t('dataLists.runs.priceFinal')}: {r.prices.final.price ?? '—'} / {r.prices.final.salePrice ?? '—'}
                        </Text>}
                        {!r.prices.verified && <Text style={{ color: colors.error }}>{t('dataLists.runs.priceMismatch')}</Text>}
                      </View>
                    )}
                  </View>
                </View>
              ))}
              {filteredRecords.length > recordLimit && (
                <TouchableOpacity onPress={() => setRecordLimit(recordLimit + RECORD_PAGE)}>
                  <Text style={[styles.link, textStart]}>{t('dataLists.runs.showMore', { count: filteredRecords.length - recordLimit })}</Text>
                </TouchableOpacity>
              )}
              {outcome?.recordsTruncated && <Text style={[styles.hint, textStart]}>{t('dataLists.runs.recordsTruncated', { count: records.length })}</Text>}
            </View>
          )}

          {(outcome?.log?.length ?? 0) > 0 && (
            <View style={styles.block}>
              <Text style={[styles.subTitle, textStart]}>{t('dataLists.runs.log')}</Text>
              <View style={styles.logBox}>
                {outcome!.log!.map((line, i) => (
                  <Text key={i} selectable style={styles.mono}>{line}</Text>
                ))}
              </View>
            </View>
          )}

          {run.status === 'success' && !outcome && <Text style={[styles.hint, textStart]}>{t('dataLists.runs.noDetails')}</Text>}
        </View>
      )}
    </View>
  );
};

const actionTone = (action: RecordAction) => {
  const color =
    action === 'created' ? (colors.status.success as string)
    : action === 'removed' ? (colors.error as string)
    : action === 'reactivated' ? (colors.warning as string)
    : action === 'skipped' ? (colors.text.secondary as string)
    : (colors.primary as string);
  return { color, backgroundColor: color + '1A' };
};

const MONO = Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' });

const getStyles = () =>
  StyleSheet.create({
    flex: { flex: 1, minWidth: 0 },
    row: { alignItems: 'center', gap: spacing.sm },
    card: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: designTokens.borderRadius.lg,
      backgroundColor: colors.background.surface,
      padding: spacing.md,
      marginBottom: spacing.md,
    },
    cardLabel: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
    cardValue: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.textDark, marginTop: 2 },
    muted: { fontFamily: typography.fontFamily.regular, color: colors.text.secondary, fontSize: typography.fontSize.sm },
    runningBanner: {
      alignItems: 'center',
      gap: spacing.sm,
      marginTop: spacing.sm,
      padding: spacing.sm,
      borderRadius: designTokens.borderRadius.md,
      backgroundColor: withAlpha(colors.primary, 0.08),
    },
    runningText: { flex: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.primary },
    hint: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, marginTop: spacing.xs },
    historyHeader: { alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm },
    sectionTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
    runCard: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: designTokens.borderRadius.md,
      backgroundColor: colors.background.surface,
      padding: spacing.sm,
      marginBottom: spacing.sm,
    },
    runTitleRow: { alignItems: 'center', gap: spacing.xs, flexWrap: 'wrap' },
    runTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
    runMeta: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, marginTop: 2 },
    badge: { paddingHorizontal: 6, paddingVertical: 1, borderRadius: 8, backgroundColor: colors.background.secondary },
    badgeText: { fontSize: 10, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
    details: { marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border },
    detailLine: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
    countsGrid: { flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.sm },
    countCell: {
      minWidth: 76,
      flexGrow: 1,
      alignItems: 'center',
      paddingVertical: spacing.xs,
      borderRadius: designTokens.borderRadius.sm,
      backgroundColor: colors.background.secondary,
    },
    countValue: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.bold, color: colors.textDark },
    countLabel: { fontSize: 10, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
    block: { marginTop: spacing.md },
    subTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.textDark, marginBottom: spacing.xs },
    errorBox: {
      marginTop: spacing.md,
      padding: spacing.sm,
      borderRadius: designTokens.borderRadius.sm,
      backgroundColor: withAlpha(colors.error, 0.07),
    },
    mono: { fontFamily: MONO, fontSize: 11, color: colors.textDark },
    stack: { marginTop: spacing.xs, color: colors.text.secondary },
    link: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.primary, marginTop: spacing.xs },
    listRow: { paddingVertical: 6, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
    listPrimary: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.textDark },
    listSecondary: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, marginTop: 1 },
    chips: { flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.xs },
    chip: { paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: 14, borderWidth: 1, borderColor: colors.border },
    chipActive: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary, 0.08) },
    chipText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
    chipTextActive: { color: colors.primary },
    recordRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: 6, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
    actionTag: {
      fontSize: 10,
      fontFamily: typography.fontFamily.semibold,
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 6,
      overflow: 'hidden',
      minWidth: 70,
      textAlign: 'center',
    },
    logBox: { padding: spacing.sm, borderRadius: designTokens.borderRadius.sm, backgroundColor: colors.background.secondary, gap: 2 },
  });
